import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Colors } from '@/components/ui/types';

import {
  StatActionsProps,
  StatDescriptionProps,
  StatFigureProps,
  StatProps,
  StatTitleProps,
  StatValueProps,
  StatsDirection,
  StatsProps,
} from './types';

const directionClasses: Record<StatsDirection, string> = {
  horizontal: 'stats-horizontal',
  vertical: 'stats-vertical',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'text-neutral',
  primary: 'text-primary',
  secondary: 'text-secondary',
  accent: 'text-accent',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-error',
};

const StatsBase = forwardRef<HTMLDivElement, StatsProps>(
  ({ className, direction = 'horizontal', children, ...props }, ref) => {
    const classes = twMerge('stats', directionClasses[direction], className);

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  },
);

const Stat = forwardRef<HTMLDivElement, StatProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('stat', className)} {...props}>
      {children}
    </div>
  ),
);

const Title = forwardRef<HTMLDivElement, StatTitleProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('stat-title', className)} {...props}>
      {children}
    </div>
  ),
);

const Value = forwardRef<HTMLDivElement, StatValueProps>(
  ({ className, color = 'default', children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('stat-value', colorClasses[color], className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Description = forwardRef<HTMLDivElement, StatDescriptionProps>(
  ({ className, color = 'default', children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('stat-desc', colorClasses[color], className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Figure = forwardRef<HTMLDivElement, StatFigureProps>(
  ({ className, color = 'default', children, ...props }, ref) => (
    <div
      ref={ref}
      className={twMerge('stat-figure', colorClasses[color], className)}
      {...props}
    >
      {children}
    </div>
  ),
);

const Actions = forwardRef<HTMLDivElement, StatActionsProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('stat-actions', className)} {...props}>
      {children}
    </div>
  ),
);

StatsBase.displayName = 'Stats';
Stat.displayName = 'Stats.Stat';
Title.displayName = 'Stats.Title';
Value.displayName = 'Stats.Value';
Description.displayName = 'Stats.Description';
Figure.displayName = 'Stats.Figure';
Actions.displayName = 'Stats.Actions';

export const Stats = Object.assign(StatsBase, {
  Stat,
  Title,
  Value,
  Description,
  Figure,
  Actions,
});
